<template>
  <div class="relative w-full">
    <!-- Input Field -->
    <WInput
      ref="autocompleteInput"
      v-model="addressInput"
      :disabled="isLoading || disabled"
      :placeholder="label"
      clearable
      autofocus
      :prefix="themeIcons.search"
      class="min-h-[2.75rem] w-full md:min-w-[20rem]"
      @focus="onFocus"
      @blur="onBlur"
      @input="debouncedOnChange"
      @keydown="onKeyPress"
      @keyup="onKeyUp"
      @clear="clear"
    />

    <!-- Dropdown Menu -->
    <div
      v-if="isMenuVisible"
      class="absolute z-40 mt-1 max-h-[40rem] w-full overflow-auto rounded border bg-white shadow-lg"
    >
      <div v-if="!results?.length" class="p-4 text-center text-gray-600">
        <p>Search for any place or 3 word address</p>
        <p>e.g. 65 Alfred Road</p>
        <p>Notting Hill, London</p>
        <p><span class="text-[#e11f26]">///</span>limit.broom.flip</p>
      </div>

      <ul v-else class="divide-y divide-gray-200">
        <template v-for="(suggestion, index) in results">
          <li
            v-if="'words' in suggestion"
            :key="suggestion?.words || index"
            @mouseenter="focusedItem = index"
            @mouseleave="focusedItem = -1"
            @click="saveLocation(suggestion)"
            :class="['cursor-pointer p-2', focusedItem === index ? 'bg-gray-200' : 'bg-white']"
          >
            <!-- w3w coordinates or suggestions -->
            <div v-if="suggestion && suggestion?.words" class="font-semibold text-gray-800">
              <span class="text-[#e11f26]">///</span> {{ suggestion?.words }}
              <div v-if="suggestion?.nearestPlace" class="text-sm text-gray-600">
                near: {{ suggestion.nearestPlace }}
              </div>
            </div>
            <div
              v-else-if="
                'coordinates' in suggestion &&
                suggestion.coordinates &&
                typeof suggestion.coordinates === 'object' &&
                'lat' in suggestion.coordinates &&
                suggestion?.coordinates?.lat
              "
              class="font-semibold text-gray-800"
            >
              <span class="text-[#e11f26]">///</span> {{ suggestion?.words }}
              <div v-if="suggestion?.nearestPlace" class="text-sm text-gray-600">
                near: {{ suggestion.nearestPlace }}
              </div>
            </div>
          </li>
          <li
            v-if="'Type' in suggestion"
            :key="suggestion?.Id || index"
            @mouseenter="focusedItem = index"
            @mouseleave="focusedItem = -1"
            @click="saveLocation(suggestion)"
            :class="[
              'flex cursor-pointer items-center justify-start p-2',
              focusedItem === index ? 'bg-gray-200' : 'bg-white',
            ]"
          >
            <WidgetIcon
              :icon="themeIcons.marker"
              class="mr-2 h-5 w-5"
              :class="
                focusedItem === index
                  ? checkTheme('jaguar', 'jlr')
                    ? 'text-negative'
                    : 'text-primary'
                  : 'text-fontPrimary'
              "
            />
            <div class="flex flex-col items-start">
              <span class="font-semibold" v-html="getLoqateHighlighted(suggestion, 'text')"></span>
              <div class="text-sm text-gray-600" v-html="getLoqateHighlighted(suggestion, 'description')"></div>
            </div>
            <WidgetIcon
              v-if="suggestion?.Type === 'Container'"
              :icon="themeIcons.chevronRight"
              class="ml-auto h-5 w-5"
              :class="
                focusedItem === index
                  ? checkTheme('jaguar', 'jlr')
                    ? 'text-negative'
                    : 'text-primary'
                  : 'text-fontPrimary'
              "
            />
          </li>
          <li
            v-else-if="
              'structured_formatting' in suggestion &&
              suggestion?.structured_formatting &&
              typeof suggestion?.structured_formatting === 'object'
            "
            :key="index"
            @mouseenter="focusedItem = index"
            @mouseleave="focusedItem = -1"
            @click="saveLocation(suggestion)"
            :class="[
              'flex cursor-pointer items-center justify-start p-2',
              focusedItem === index ? 'bg-gray-200' : 'bg-white',
            ]"
          >
            <template v-if="suggestion?.structured_formatting">
              <WidgetIcon
                :icon="themeIcons.marker"
                class="mr-2 h-5 w-5"
                :class="
                  focusedItem === index
                    ? checkTheme('jaguar', 'jlr')
                      ? 'text-negative'
                      : 'text-primary'
                    : 'text-fontPrimary'
                "
              />
              <div class="flex flex-col items-start">
                <span class="font-semibold" v-html="getMainFormattedText(suggestion.structured_formatting)"></span>
                <div
                  class="text-sm text-gray-600"
                  v-html="getSecondaryFormattedText(suggestion.structured_formatting)"
                ></div>
              </div>
            </template>
          </li>
        </template>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, watch, onMounted, onUnmounted, nextTick } from 'vue';
  import { convertTo3wa, GooglePlaceResult, useAddressSearch, W3WSuggestion } from 'src/composables/googleMaps';
  import WInput from './Input.vue';
  import WidgetIcon from './WidgetIcon.vue';
  import { formatResult, ILocationInfo } from 'src/utils/map';
  import { checkTheme, themeIcons } from 'src/utils/config';
  import { LoqateSuggestion } from 'src/types/loqate';
  import { getAddressById, searchAddress as searchLoqateAddress } from 'src/utils/loqate';
  import { useDebounceFn } from '@vueuse/core';

  const props = defineProps({
    id: { type: String, default: 'google-autocomplete' },
    text: { type: String, default: '' },
    className: { type: String, default: '' },
    label: { type: String, default: 'Start by entering an address' },
    disabled: { type: Boolean, default: false },
    clearable: { type: Boolean, default: true },
    types: { type: String, default: 'address' },
    country: { type: [String, Array], default: () => '' },
    icon: { type: String, default: '' },
    location: { type: Object, default: () => ({}) },
    useLoqate: { type: Boolean, default: true },
  });

  const emit = defineEmits(['placeChanged', 'keyup', 'keypress', 'change', 'focus', 'blur', 'clear']);

  const autocompleteInput = ref<HTMLInputElement | null>(null);
  const addressInput = ref<string>(props.text || '');
  const results = ref<(GooglePlaceResult | W3WSuggestion | LoqateSuggestion)[]>([]);
  const isMenuVisible = ref<boolean>(false);
  const focusedItem = ref<number>(-1);

  const { search, getPlaceDetails, convertToCoordinates, placeGeocodingLatLng, isLoading } = useAddressSearch({
    useLoqate: props.useLoqate,
  });

  function onFocus() {
    isMenuVisible.value = true;
    emit('focus');
  }

  function onBlur() {
    emit('blur');
  }

  async function onChange() {
    emit('change', addressInput.value);
    results.value = (await search(addressInput.value)) || [];
    isMenuVisible.value = true;
    focusedItem.value = 0;
  }
  const debouncedOnChange = useDebounceFn(onChange, 300);

  async function saveLocation(location: GooglePlaceResult | W3WSuggestion | LoqateSuggestion) {
    if ('Id' in location && 'Type' in location && 'Text' in location) {
      if (location.Type === 'Container') {
        results.value = (await searchLoqateAddress(location.Text, location.Id)) || [];
        nextTick(() => {
          if (autocompleteInput.value) {
            autocompleteInput.value.focus();
          }
          isMenuVisible.value = true;
          focusedItem.value = 0;
        });
        return;
      }

      const details = await getAddressById(location.Id);
      if (details) {
        const placeResult = {
          formatted_address: details.Label?.replaceAll('\n', ' ').trim(),
          address_components: [
            { long_name: details.BuildingNumber, short_name: details.BuildingNumber, types: ['street_number'] },
            { long_name: details.Street, short_name: details.Street, types: ['route'] },
            { long_name: details.City, short_name: details.City, types: ['locality'] },
            {
              long_name: details.AdminAreaName,
              short_name: details.AdminAreaName,
              types: ['administrative_area_level_1'],
            },
            { long_name: details.CountryName, short_name: details.CountryIso2, types: ['country'] },
            { long_name: details.PostalCode, short_name: details.PostalCode, types: ['postal_code'] },
          ].filter((c) => c.long_name),
        };

        const words = await convertTo3wa(
          new google.maps.LatLng(parseFloat(details.Field1), parseFloat(details.Field2))
        );

        const finalResult = {
          ...(await formatResult({ ...placeResult, words: words?.words || '' })),
          latitude: parseFloat(details.Field1),
          longitude: parseFloat(details.Field2),
          formattedAddress: details.Label.replaceAll('\n', ' ').trim(),
        } satisfies ILocationInfo;
        addressInput.value = details.Label.replaceAll('\n', ' ').trim();
        emit('placeChanged', finalResult);
        closeMenu();
      }
      return;
    }
    if (
      'formattedAddress' in location &&
      location?.formattedAddress &&
      typeof location?.formattedAddress === 'string'
    ) {
      addressInput.value = location.formattedAddress;
    } else if ('words' in location && location?.words) {
      addressInput.value = `///${location.words}`;
    }

    if ('words' in location && location?.words) {
      const translatedWords = await convertToCoordinates(location.words);
      if (translatedWords?.coordinates) {
        const places = await placeGeocodingLatLng(
          new google.maps.LatLng(translatedWords.coordinates.lat, translatedWords.coordinates.lng)
        );
        const formattedGoogleAddress = places?.[0] || {};
        const finalResult = {
          ...(await formatResult({ ...formattedGoogleAddress, words: location.words })),
          latitude: translatedWords.coordinates.lat,
          longitude: translatedWords.coordinates.lng,
        };
        focusedItem.value = -1;
        emit('placeChanged', finalResult);
        closeMenu();
        return;
      }
    }

    if (!('words' in location) && !location?.place_id && location?.latitude && location?.longitude) {
      const places = await placeGeocodingLatLng(new google.maps.LatLng(location.latitude, location.longitude));
      const finalResult = {
        ...(await formatResult(places?.[0] || {})),
        latitude: location.latitude,
        longitude: location.longitude,
      };
      focusedItem.value = -1;
      emit('placeChanged', finalResult);
      closeMenu();
      return;
    }

    if (!('words' in location) && location?.place_id) {
      const details = await getPlaceDetails(location.place_id);
      const finalResult = {
        ...(await formatResult(details || {})),
        latitude: location.latitude,
        longitude: location.longitude,
      };
      focusedItem.value = -1;
      emit('placeChanged', finalResult);
      closeMenu();
    }
  }

  function getMainFormattedText(result: GooglePlaceResult['structured_formatting']) {
    if (
      result?.main_text_matched_substrings &&
      Array.isArray(result?.main_text_matched_substrings) &&
      result.main_text_matched_substrings.length > 0
    ) {
      const { offset, length } = result.main_text_matched_substrings[0];
      const mainText = result.main_text;
      const firstPart = mainText.slice(0, offset);
      const highlightedPart = mainText.slice(offset, offset + length);
      const lastPart = mainText.slice(offset + length);
      return `${firstPart}<span class="${checkTheme('jaguar', 'jlr') ? 'text-negative' : 'text-primary'} font-semibold">${highlightedPart}</span>${lastPart}`;
    }
    return result?.main_text;
  }

  function getSecondaryFormattedText(result: GooglePlaceResult['structured_formatting']) {
    if (
      result?.secondary_text_matched_substrings &&
      Array.isArray(result.secondary_text_matched_substrings) &&
      result.secondary_text_matched_substrings.length > 0
    ) {
      const { offset, length } = result.secondary_text_matched_substrings[0];
      const secondaryText = result?.secondary_text || '';
      const firstPart = secondaryText.slice(0, offset);
      const highlightedPart = secondaryText.slice(offset, offset + length);
      const lastPart = secondaryText.slice(offset + length);
      return `${firstPart}<span class="${checkTheme('jaguar', 'jlr') ? 'text-negative' : 'text-primary'} font-semibold">${highlightedPart}</span>${lastPart}`;
    }
    return result?.secondary_text;
  }

  function highlightLoqate(text: string, rangesStr: string) {
    if (!rangesStr || !text) {
      return text;
    }
    const ranges = rangesStr.split(',').map((r) => r.split('-').map((n) => parseInt(n, 10)));
    let result = '';
    let lastIndex = 0;
    ranges.forEach(([start, end]) => {
      if (isNaN(start) || isNaN(end)) return;
      result += text.substring(lastIndex, start);
      result += `<span class="${
        checkTheme('jaguar', 'jlr') ? 'text-negative' : 'text-primary'
      } font-semibold">${text.substring(start, end)}</span>`;
      lastIndex = end;
    });
    result += text.substring(lastIndex);
    return result;
  }

  function getLoqateHighlighted(suggestion: LoqateSuggestion, part: 'text' | 'description') {
    if (!suggestion.Highlight) {
      return part === 'text' ? suggestion.Text : suggestion.Description;
    }
    const [textHighlight, descriptionHighlight] = suggestion.Highlight.split(';');
    if (part === 'text') {
      return highlightLoqate(suggestion.Text, textHighlight);
    }
    return highlightLoqate(suggestion.Description, descriptionHighlight);
  }

  function onKeyPress(event: KeyboardEvent) {
    emit('keypress', event);
  }

  function onKeyUp(event: KeyboardEvent) {
    emit('keyup', event);
  }

  function closeMenu() {
    isMenuVisible.value = false;
  }

  function clear() {
    addressInput.value = '';
    emit('clear');
  }

  function focus() {
    autocompleteInput.value?.focus();
  }

  function blur() {
    autocompleteInput.value?.blur();
  }

  function handleKeyboardNavigation(event: KeyboardEvent) {
    if (isMenuVisible.value && results.value.length > 0) {
      event.preventDefault();
      event.stopPropagation();
      if (event.key === 'ArrowDown') {
        focusedItem.value = (focusedItem.value + 1) % results.value.length;
      } else if (event.key === 'ArrowUp') {
        focusedItem.value = focusedItem.value <= 0 ? results.value.length - 1 : focusedItem.value - 1;
      } else if (event.key === 'Enter') {
        if (focusedItem.value >= 0) {
          saveLocation(results.value[focusedItem.value]);
          blur();
        }
      } else if (event.key === 'Escape') {
        blur();
      }
    }
  }

  watch(
    () => props.text,
    async () => {
      addressInput.value = props.text;
      results.value = (await search(addressInput.value)) || [];
      isMenuVisible.value = false;
      focusedItem.value = 0;
    }
  );

  onMounted(async () => {
    window.addEventListener('keyup', handleKeyboardNavigation);
    if (addressInput.value) {
      results.value = (await search(addressInput.value)) || [];
      focusedItem.value = 0;
    }
  });
  onUnmounted(() => {
    window.removeEventListener('keyup', handleKeyboardNavigation);
  });

  defineExpose({ focus, blur });
</script>
